/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, FullscreenDialog, Typography2, breakpoints } from '@coursera/cds-core';
import { FilterIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/program-skills-objectives';

import ProductFilters from './ProductFilters';
import type { FilterDropdownOptionsMap } from './generateFilterDropdownOptions';
import type { FilterKeys, FilterState } from './useProductFilterState';

const useStyles = () => {
  return {
    mobileFilterContent: css`
      margin-top: var(--cds-spacing-100);
    `,
    mobileFilters: css`
      display: none;
      ${breakpoints.down('xs')} {
        display: flex;
        column-gap: var(--cds-spacing-100);
        align-items: center;
      }
    `,
    desktopFilters: css`
      display: block;
      ${breakpoints.down('xs')} {
        display: none;
      }
    `,
    dialogHeader: css`
      .cds-FullscreenDialogHeader-slot1 {
        display: flex;
        align-items: center;
      }
    `,
  };
};

type Props = {
  filterValues: FilterState;
  setIsMobileFiltersModalOpen: (isMobileFiltersModalOpen: boolean) => void;
  setFilterValue: (key: FilterKeys, value: string) => void;
  filterDropdownOptions: FilterDropdownOptionsMap;
  isMobileFiltersModalOpen: boolean;
  resetFilterValues: () => void;
};

// This component controls showing a mobile and desktop version of the product filters.
const MobileDesktopProductFilters = ({
  filterValues,
  setIsMobileFiltersModalOpen,
  setFilterValue,
  resetFilterValues,
  isMobileFiltersModalOpen,
  filterDropdownOptions,
}: Props) => {
  const styles = useStyles();
  return (
    <>
      <div css={styles.mobileFilters}>
        <Button onClick={() => setIsMobileFiltersModalOpen(true)} icon={<FilterIcon />} iconPosition="after">
          {_t('Filter')}
        </Button>
        <Typography2 component="p" color="supportText" variant="bodyPrimary">
          {_t('#{count} active filter(s)', {
            count: Object.values(filterValues).filter((filter) => !!filter).length,
          })}
        </Typography2>
        <FullscreenDialog
          open={isMobileFiltersModalOpen}
          onClose={() => {
            setIsMobileFiltersModalOpen(false);
          }}
        >
          <FullscreenDialog.Header
            slot1={
              <Typography2
                variant="subtitleMedium"
                color="highlightBlue"
                component="h2"
                data-testid="skill-page-mobile-filter-header"
              >
                {_t('Filter by')}
              </Typography2>
            }
            css={styles.dialogHeader}
          />
          <FullscreenDialog.Content css={styles.mobileFilterContent}>
            <ProductFilters
              filterValues={filterValues}
              onFilterChange={setFilterValue}
              onResetClick={resetFilterValues}
              dropdownOptions={filterDropdownOptions}
            />
          </FullscreenDialog.Content>
        </FullscreenDialog>
      </div>
      <div css={styles.desktopFilters}>
        <ProductFilters
          filterValues={filterValues}
          onFilterChange={setFilterValue}
          onResetClick={resetFilterValues}
          dropdownOptions={filterDropdownOptions}
        />
      </div>
    </>
  );
};

export default MobileDesktopProductFilters;
